import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// Types
export interface Alert {
  id: number;
  userId: number;
  userName?: string;
  alertType: number;
  alertTypeName: string;
  title: string;
  message: string;
  priority: number;
  priorityName: string;
  status: number;
  statusName: string;
  entityType?: number;
  entityTypeName?: string;
  entityId?: number;
  entityName?: string;
  readAt?: string;
  handledAt?: string;
  handledNotes?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface AlertFilterDto {
  status?: number;
  priority?: number;
  alertType?: number;
}

export interface HandleAlertDto {
  handledNotes?: string;
}

// Enums
export enum AlertType {
  ClientAssigned = 2,
  ClientDocumentUploaded = 3,
  OutstandingDebtOverdue = 9
}

export enum AlertStatus {
  Unread = 1,
  Read = 2,
  Handled = 3
}

export enum AlertPriority {
  High = 1,
  Medium = 2,
  Low = 3
}

export enum EntityType {
  Lead = 1,
  Client = 2,
  Case = 3,
  General = 4
}

// API calls
export const alertsApi = {
  // Get alerts with filters
  getAlerts: async (filter: AlertFilterDto = {}): Promise<Alert[]> => {
    const params = new URLSearchParams();
    if (filter.status !== undefined) params.append('status', filter.status.toString());
    if (filter.priority !== undefined) params.append('priority', filter.priority.toString());
    if (filter.alertType !== undefined) params.append('alertType', filter.alertType.toString());

    const url = params.toString()
      ? `${API_BASE_URL}/alerts?${params.toString()}`
      : `${API_BASE_URL}/alerts`;

    const response = await axios.get(url);
    return response.data;
  },

  // Get unread alerts
  getUnreadAlerts: async (): Promise<Alert[]> => {
    const response = await axios.get(`${API_BASE_URL}/alerts/unread`);
    return response.data;
  },

  // Get unread count
  getUnreadCount: async (): Promise<number> => {
    const response = await axios.get(`${API_BASE_URL}/alerts/unread/count`);
    return response.data;
  },

  // Get alert by ID
  getAlert: async (id: number): Promise<Alert> => {
    const response = await axios.get(`${API_BASE_URL}/alerts/${id}`);
    return response.data;
  },

  // Mark as read
  markAsRead: async (id: number): Promise<Alert> => {
    const response = await axios.put(`${API_BASE_URL}/alerts/${id}/mark-as-read`);
    return response.data;
  },

  // Mark as handled
  markAsHandled: async (id: number, data: HandleAlertDto): Promise<Alert> => {
    const response = await axios.put(`${API_BASE_URL}/alerts/${id}/mark-as-handled`, data);
    return response.data;
  },

  // Delete alert
  deleteAlert: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/alerts/${id}`);
  },

  // Mark all as read
  markAllAsRead: async (alertIds: number[]): Promise<void> => {
    await Promise.all(alertIds.map(id => alertsApi.markAsRead(id)));
  }
};

// Helper functions
export const getAlertTypeIcon = (type: AlertType): string => {
  switch (type) {
    case AlertType.ClientAssigned: return '👥';
    case AlertType.ClientDocumentUploaded: return '📄';
    case AlertType.OutstandingDebtOverdue: return '💰';
    default: return '🔔';
  }
};

export const getAlertTypeName = (type: AlertType): string => {
  switch (type) {
    case AlertType.ClientAssigned: return 'לקוח הוקצה';
    case AlertType.ClientDocumentUploaded: return 'מסמכים הועלו';
    case AlertType.OutstandingDebtOverdue: return 'חוב פתוח';
    default: return 'לא ידוע';
  }
};

export const getPriorityColor = (priority: AlertPriority): string => {
  switch (priority) {
    case AlertPriority.High: return '#dc2626';      // 1 = High
    case AlertPriority.Medium: return '#d97706';    // 2 = Medium
    case AlertPriority.Low: return '#0073ea';       // 3 = Low
    default: return '#6c757d';
  }
};

export const getPriorityLabel = (priority: AlertPriority): string => {
  switch (priority) {
    case AlertPriority.High: return 'גבוה';
    case AlertPriority.Medium: return 'בינוני';
    case AlertPriority.Low: return 'נמוך';
    default: return 'לא ידוע';
  }
};

export const getEntityTypeLabel = (type: EntityType): string => {
  switch (type) {
    case EntityType.Lead: return 'ליד';
    case EntityType.Client: return 'לקוח';
    case EntityType.Case: return 'תיק';
    case EntityType.General: return 'כללי';
    default: return 'לא ידוע';
  }
};

// Format relative time in Hebrew
export const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'לפני כמה שניות';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return diffInMinutes === 1 ? 'לפני דקה' : `לפני ${diffInMinutes} דקות`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return diffInHours === 1 ? 'לפני שעה' : `לפני ${diffInHours} שעות`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return diffInDays === 1 ? 'אתמול' : `לפני ${diffInDays} ימים`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return diffInWeeks === 1 ? 'לפני שבוע' : `לפני ${diffInWeeks} שבועות`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  return diffInMonths === 1 ? 'לפני חודש' : `לפני ${diffInMonths} חודשים`;
};

// Error handler
export const handleApiError = (error: any): string => {
  if (error.response) {
    return error.response.data?.message || error.response.data || 'שגיאה בטעינת נתונים';
  } else if (error.request) {
    return 'לא ניתן להתחבר לשרת. אנא בדוק את החיבור לאינטרנט';
  } else {
    return error.message || 'שגיאה לא צפויה';
  }
};

export default alertsApi;
